import { lazy, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { Col, Row } from 'react-bootstrap';
import { MediFindMoments } from '@medifind/ads';
import { BasicPageBody, BasicPageContainer, BlockBody, BlockContainer, BlockHeader } from '@medifind/containers';
import {
  AreasOfExpertiseBlock,
  DoctorProfileAds,
  DoctorProfileAreasOfFocus,
  DoctorProfileClinicalResearch,
  DoctorProfileNavMenu,
  DoctorProfileOverview,
  HighlyRatedBlock,
  TopDataContainer,
} from '@medifind/doctors';
import { clinicalTrialSearch, getDoctor, publicationSearch } from '@medifind/interface';
import { Share } from '@medifind/popups';
import { toSlug, useRouter } from '@medifind/router';
import { ClaimedProfileBadge } from '@medifind/search-results';
import { BreadcrumbLinks, DoctorJsonLd, SEO } from '@medifind/seo';
import {
  BackButton,
  Bookmark,
  DoctorInsurance,
  Headshot,
  LocationAccordion,
  MFLazyLoad,
  MFLink,
  ProfileConditionBadgeContainer,
  ScoreBars,
  ScoreLabel,
  Skeleton,
} from '@medifind/shared-basic-components';
import { isEmpty, isPrerender, useMediaQueryDown } from '@medifind/utils';
import { isValidProjectId, scrollToId, useMediaQueryUp } from '@medifind/utils';
import { setShareBookmarkData, useAuthentication } from '@medifind/zustand';
import { selectRouteParams, useRouteStore } from '@medifind/zustand';
import styles from './doctors-single.module.scss';
const RelatedDoctors = lazy(() => import('@medifind/lazy-loadable/lib/shared-basic-components/LazyRelatedDoctors'));

const OrgTitle = ({ name, website, doctor }) => {
  return !doctor || name ? (
    <div className={styles['sub-name']}>
      {doctor ? (
        <>
          {name ? (
            website ? (
              <div>
                <MFLink to={website} target="blank" className={styles['org-link']}>
                  {name}
                </MFLink>
              </div>
            ) : (
              name
            )
          ) : null}
        </>
      ) : (
        <Skeleton />
      )}
    </div>
  ) : null;
};

const getBreadcrumbItems = ({ primarySpecialtyLookup = {}, address = {}, doctor = {} }) => {
  if (!doctor) {
    return [{ name: 'Homepage', link: '/' }];
  }
  const { slug, role } = primarySpecialtyLookup;
  const { country2Code, location, city, stateProvinceCode } = address;
  const { displayName } = doctor ?? {};

  const baseLink = `/specialty/${slug}/${country2Code}`;
  const specialtyLink = location && stateProvinceCode ? `${baseLink}/${stateProvinceCode}${city ? '/' + city : ''}` : baseLink;

  return [
    { name: 'Homepage', link: '/' },
    primarySpecialtyLookup &&
      !isEmpty(primarySpecialtyLookup) && {
        name: `${role}s ${location || country2Code}`,
        link: specialtyLink,
      },
    { name: displayName || name, link: null },
  ].filter(Boolean);
};

/* __ROUTE_PATH__=/doctors/:personSlug/:personId */
/* __ROUTE_PATH__=/(conditions|procedures)/:codingSlug/:projectId/doctors/:personSlug/:personId */
/* __RESOURCE_PRELOAD__={"loadRoute": "/doctors/:personSlug/:personId", "params": {"href":"/api/entity/doctor/$personId", "as":"fetch","crossorigin":"anonymous"}} */
/* __RESOURCE_PRELOAD__={"loadRoute": "/doctors/:personSlug/:personId", "params": {"href":"/api/campaigns?personPersonifyId=$personId", "as":"fetch","crossorigin":"anonymous"}} */
const DoctorSingle = () => {
  const router = useRouter();
  const { query } = router;
  const { personId: queryPersonId, personSlug, projectId, codingSlug, insurance: queryInsurance } = query || {};
  const [errorCode, setErrorCode] = useState(false);
  const { codingName, codingType } = useRouteStore(selectRouteParams);
  const { profile } = useAuthentication();
  const preventFetch = useRef(false);
  const mediumBreakpoint = useMediaQueryDown('md');
  const idToScroll = router.location.hash.slice(1);
  const isLargeAndUp = useMediaQueryUp('lg') || isPrerender;
  const isTabletAndAbove = useMediaQueryUp('md') || isPrerender;

  const { data: doctor } = useQuery({
    queryKey: ['getDoctor', queryPersonId, projectId, preventFetch],
    queryFn: async () => {
      try {
        const result = projectId ? await getDoctor(queryPersonId, projectId) : await getDoctor(queryPersonId);
        if (projectId && !result) {
          router.history.replace(`/doctors/${personSlug}/${queryPersonId}`);
        } else {
          if (result.personId && queryPersonId && queryPersonId !== result.personId) {
            preventFetch.current = true;
            router.history.replace(router.location.pathname.replace(queryPersonId, result.personId) + router.location.search);
          }
          return result;
        }
      } catch (e) {
        if (e.errorCode === 429) setErrorCode(e.code);
        else setErrorCode(404);
      }
    },
    enabled: !!queryPersonId && !preventFetch.current,
  });

  //  usePageHooks(doctor);

  useEffect(() => {
    scrollToId(idToScroll, 0, 40, null, 'auto');
  }, [doctor, router]);

  const { data: articles } = useQuery({
    queryKey: ['publicationSearch', queryPersonId, preventFetch],
    queryFn: () => publicationSearch({ personId: queryPersonId, size: 2, recent: false }).catch(() => {}),
    enabled: !!queryPersonId && !preventFetch.current,
  });

  const { data: trials } = useQuery({
    queryKey: ['clinicalTrialSearch', queryPersonId, preventFetch],
    queryFn: () => clinicalTrialSearch({ personId: queryPersonId, noStatus: true, size: 50 }).catch(() => {}),
    select: (results) => {
      results.resultsRecruiting = results.results.filter((t) => t.status === 'Recruiting');
      results.results = results.results.filter((t) => t.status !== 'Recruiting');
      return results;
    },
    enabled: !!queryPersonId && !preventFetch.current,
  });

  const {
    personId,
    name,
    displayName,
    address,
    primaryOrgName,
    primaryOrgWebsite,
    specialties,
    primarySpecialtyLookup,
    healthSystem,
    conditions,
    demographics,
    biography,
    phone,
    score,
    acceptsMedicare,
    isPremium,
    highlyRatedConditionsCount,
    insurance,
    roles,
    addresses,
    areasOfFocus,
  } = doctor || {};

  const conditionTier = score ? Math.min(Math.floor(score / 25) + 1, 4) : null;

  const isUSBased = address?.country2Code === 'US';

  useEffect(() => {
    setShareBookmarkData({
      bookmark: {
        bookmarkName: name,
        data: personId,
        ...(projectId && codingName
          ? {
              secondaryLookupId: projectId,
              secondaryLookupData: {
                projectId,
                codingSlug,
                codingName: codingName,
                codingType,
                type: 'projectId',
              },
            }
          : {}),
        type: 'doctor',
      },
      share: {
        bookmarkName: name,
        personifyId: personId,
        projectId,
        codingSlug,
        codingName: codingName,
        type: 'doctor',
      },
    });
  }, [name, personId, projectId, codingSlug, codingName, codingType]);
  if (errorCode) {
    throw new Error(errorCode);
  }

  const { resultsRecruiting: trialsResultsRecruiting } = trials || {};
  const description = biography?.length > 300 ? biography.slice(0, 297) + '...' : biography;
  const title = [
    name,
    specialties?.[0],
    address?.city ? `${address?.city}${address?.stateProvinceCode ? ', ' + address?.stateProvinceCode : ''}` : null,
    'MediFind',
  ]
    .filter(Boolean)
    .join(' | ');
  const isOrgNameEqualsHealthSystem =
    healthSystem?.name?.toLowerCase().replace(/[^a-zA-Z0-9]/g, '') === primaryOrgName?.toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
  const healthSystemWebsite = isPrerender ? healthSystem?.website : null;

  const centralBlockClasses = {
    block: styles['sub-block-container'],
    blockHeader: styles['sub-block-container__header'],
    blockBody: styles['sub-block-container__body'],
  };
  const rectangleRightAds = ({ position, isSticky = false }) => (
    <MediFindMoments
      type="rectangle"
      targeting={{
        position,
        pageType: 'provider-profile',
        specialties,
      }}
      isSticky={isSticky}
    />
  );

  const breadcrumbItems = getBreadcrumbItems({ primarySpecialtyLookup, address, doctor });

  return (
    <>
      {name && (
        <>
          <SEO
            title={title}
            noIndex={!isUSBased}
            description={description}
            canonical={process.env.NX_LOCAL_HOST + `/doctors/${toSlug(name)}/${personId}`}
          />
          <DoctorJsonLd doctor={doctor} />
        </>
      )}
      <BasicPageContainer noBackground={true} className={styles['page-container']}>
        <BasicPageBody noPadding>
          {doctor && (
            <MediFindMoments
              type={'leaderboard'}
              targeting={{
                position: 'top',
                pageType: 'provider-profile',
                specialties,
              }}
            />
          )}
          <div className={styles['profile__header']}>
            <Row className={styles['profile__top-bar']}>
              <BackButton label={mediumBreakpoint ? 'Back' : 'Back to results'} color="transparent-blue" buttonType="box" />
              <div style={{ margin: 0, padding: 0, marginLeft: 'auto' }} />
              <Bookmark color="transparent-blue" buttonType="box" iconOnlyAt="xs" />
              <Share color="transparent-blue" buttonType="box" iconOnlyAt="xs" />
            </Row>
            <Row noGutters className={styles['profile__header-body']}>
              <Col className={styles['headshot-tier-container']} lg={!!projectId ? 4 : 3} md={!!projectId ? 4 : 3} sm={3} xs={3}>
                {/* headshot */}
                <Headshot
                  doctor={doctor}
                  showScore={score && score >= 5 && codingName}
                  hasProject={isValidProjectId(projectId)}
                  large={true}
                  classes={{ headshot: styles['headshot'] }}
                  tierFullCircle={true}
                />

                {/* tier / num. highly rated conditions (desktop) */}
                {!doctor && projectId ? (
                  <div className={'hide--md'} style={{ width: '200px' }}>
                    <Skeleton count={2} />
                  </div>
                ) : score && score >= 5 && codingName ? (
                  <div className={'hide--md'}>
                    <ScoreBars score={score} bigBars className={styles['score-bar']} center />

                    <div className={styles['score-label']}>
                      <ScoreLabel score={doctor.score} classes={{ label: styles['score-label--force'] }} codingName={codingName} />
                    </div>
                  </div>
                ) : (
                  <HighlyRatedBlock doctor={doctor} count={highlyRatedConditionsCount} className={'hide--md'} />
                )}
              </Col>
              <Col
                lg={!!projectId ? 8 : 9}
                md={!!projectId ? 8 : 9}
                sm={9}
                xs={9}
                className={classNames(styles['doctor-metadata-container'])}
              >
                {/* name */}
                {!doctor || name ? (
                  <h1 className={styles['name']}>
                    <span className={styles['text']}>{doctor ? displayName || name : <Skeleton />}</span>
                    <span className={styles['name__tick']}>
                      <ClaimedProfileBadge doctor={doctor} />
                    </span>
                  </h1>
                ) : null}

                {/* healthSystem */}
                <OrgTitle
                  doctor={doctor}
                  name={healthSystem?.name}
                  website={isOrgNameEqualsHealthSystem ? healthSystemWebsite ?? primaryOrgWebsite : healthSystemWebsite}
                />
                {/* employer */}
                {!isOrgNameEqualsHealthSystem && <OrgTitle doctor={doctor} name={primaryOrgName} website={primaryOrgWebsite} />}

                {/* city/state/country */}
                {!doctor || address ? (
                  <div className={styles['sub-name']}>
                    {!doctor ? (
                      <Skeleton width="50%" />
                    ) : address.city && address.stateProvinceCode ? (
                      `${address.city}, ${address.stateProvinceCode}`
                    ) : address.country ? (
                      address.country
                    ) : null}
                  </div>
                ) : null}
                {/* tier conditions (desktop/mobile) */}
                {score && codingName ? (
                  <div className={'hide--up--lg'}>
                    <ScoreBars score={score} bigBars className={styles['score-bar']} />

                    <div className={styles['score-label']}>
                      <ScoreLabel score={doctor.score} classes={{ label: styles['score-label--force'] }} /> in{' '}
                      <span className={styles[`tier-${conditionTier}`]}>{codingName}</span>
                    </div>
                  </div>
                ) : null}
                <div className={`hide--xs`}>
                  {/* Condition Badges (desktop) */}
                  {doctor?.centerOfExcellence && <ProfileConditionBadgeContainer badgeList={doctor.centerOfExcellence} />}
                  {/* num. highly rated conditions (desktop) */}
                  {!(score && codingName) && highlyRatedConditionsCount ? (
                    <HighlyRatedBlock count={highlyRatedConditionsCount} className={'hide--up--lg'} />
                  ) : null}
                </div>
                {isTabletAndAbove && <TopDataContainer doctor={doctor} trialsResultsRecruiting={trialsResultsRecruiting} hide="sm" />}
              </Col>
              <div className={`hide--up--sm`} style={{ width: '100%' }}>
                {/* num. highly rated conditions (mobile) */}
                {doctor ? (
                  <>
                    {!(score && codingName) && highlyRatedConditionsCount ? <HighlyRatedBlock count={highlyRatedConditionsCount} /> : null}
                  </>
                ) : (
                  <Skeleton count={1} height={35} />
                )}
                {/* Condition Badges (mobile) */}
                {doctor?.centerOfExcellence && <ProfileConditionBadgeContainer badgeList={doctor.centerOfExcellence} />}
              </div>
              {!isTabletAndAbove && <TopDataContainer doctor={doctor} trialsResultsRecruiting={trialsResultsRecruiting} hide="up--md" />}
            </Row>
          </div>
          <Row></Row>
          <Row noGutters>
            {/* data blocks (bio, insurance, etc.) */}
            <Col lg={{ span: 8 }} md={{ span: 12 }} className={styles['profile__body__center-column']}>
              <BlockContainer>
                {/* nav menu */}
                <DoctorProfileNavMenu showInsuranceSection={isUSBased} showAOESection={!isLargeAndUp} />

                <MFLazyLoad height={1000} noSuspense={!!idToScroll}>
                  {/* biography */}
                  {!doctor || biography ? (
                    <>
                      <DoctorProfileOverview classes={centralBlockClasses} doctor={doctor} />
                    </>
                  ) : null}

                  {!isLargeAndUp && doctor && rectangleRightAds({ position: 'right1' })}

                  {/* roles */}
                  {isPremium && roles?.length > 0 ? (
                    <>
                      <BlockContainer id="roles" className={styles['sub-block-container']}>
                        <BlockHeader priority={2} className={styles['sub-block-container__header']}>
                          Leadership Roles
                        </BlockHeader>
                        <BlockBody className={styles['sub-block-container__body']}>
                          <ul className={styles['list']}>
                            {roles.map((s, i) => (
                              <li key={'roles-' + i}>{s}</li>
                            ))}
                          </ul>
                        </BlockBody>
                      </BlockContainer>
                    </>
                  ) : null}

                  {/* insurance */}
                  {isUSBased ? (
                    <>
                      <DoctorInsurance
                        classes={centralBlockClasses}
                        personId={personId}
                        phone={phone}
                        displayName={displayName}
                        acceptsMedicare={acceptsMedicare}
                        demographics={demographics}
                        showCarrierDetails={queryInsurance}
                        insurance={insurance}
                        headerPriority={2}
                      />
                    </>
                  ) : null}

                  {/* location/address */}
                  <LocationAccordion
                    classes={centralBlockClasses}
                    hasData={!!doctor}
                    address={address}
                    otherAddresses={addresses}
                    primaryOrgName={primaryOrgName}
                    phone={phone}
                    hidePartialPhoneNumber={true}
                    headerPriority={2}
                  />

                  {/* Additional Areas of Focus */}
                  {areasOfFocus?.length > 0 || !doctor ? (
                    <>
                      <DoctorProfileAreasOfFocus classes={centralBlockClasses} doctor={doctor} />
                    </>
                  ) : null}

                  {/* areas of expertise (mobile) */}
                  {!isLargeAndUp && (
                    <BlockContainer id="areas-of-expertise" className={styles['sub-block-container']}>
                      <AreasOfExpertiseBlock
                        classes={{ blockHeader: styles['sub-block-container__header'], blockBody: styles['sub-block-container__body'] }}
                        headerPriority={2}
                        areasOfExpertise={conditions}
                        personId={personId}
                        displayName={displayName}
                        conditions={conditions}
                      />
                    </BlockContainer>
                  )}

                  {!isLargeAndUp && doctor && rectangleRightAds({ position: 'mid1' })}

                  {/* clinical research */}
                  <DoctorProfileClinicalResearch
                    classes={centralBlockClasses}
                    doctor={doctor}
                    articles={articles}
                    personSlug={personSlug}
                    profile={profile}
                    trials={trials}
                  />

                  {/* related doctors */}
                  <RelatedDoctors
                    classes={centralBlockClasses}
                    doctor={doctor}
                    projectId={projectId}
                    codingSlug={codingSlug}
                    bottomContent={!isLargeAndUp && doctor && rectangleRightAds({ position: 'mid2' })}
                  />
                </MFLazyLoad>
              </BlockContainer>
            </Col>

            {/* right-side self-ads (desktop) */}
            <Col lg={{ span: 4 }} md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 12 }} className={styles['profile__side-container']}>
              {isLargeAndUp && (
                <>
                  {doctor && rectangleRightAds({ position: 'right1', isSticky: true })}
                  <BlockContainer>
                    <AreasOfExpertiseBlock
                      classes={centralBlockClasses}
                      headerPriority={2}
                      areasOfExpertise={conditions}
                      personId={personId}
                      displayName={displayName}
                      conditions={conditions}
                    />
                  </BlockContainer>
                </>
              )}
              <DoctorProfileAds doctor={doctor} personId={personId} />
            </Col>
          </Row>
          {!projectId && (
            <Row noGutters>
              <Col className={styles['breadcrump-column']}>
                <BreadcrumbLinks items={breadcrumbItems} />
              </Col>
            </Row>
          )}
        </BasicPageBody>
      </BasicPageContainer>
    </>
  );
};

export default DoctorSingle;
